<script lang="ts">
  import { onMount, onDestroy, type Snippet } from 'svelte';
  import { MapEngine } from '@atlasguess/engine';

  interface Props {
    /**
     * Snippet that receives the live engine instance + a `markReady`
     * callback. Only renders once the engine is constructed, so the
     * child component can safely use the engine in its OWN onMount
     * without a race condition.
     *
     * `markReady()` fades the loading veil out. Call it when the mode
     * has everything it needs to be presentable — for the default
     * texture-based modes the engine triggers reveal automatically on
     * first texture load, so calling markReady() is optional. For
     * skip-texture modes (gray-fill country quizzes etc.) the auto-
     * reveal never fires, so the mode must call markReady() itself
     * once polygons / fills land.
     */
    children: Snippet<[MapEngine, () => void]>;
    /**
     * Skip the photo-texture fetch. Modes that hide the surface sphere
     * pass `true` to save the bandwidth; the loading veil then waits
     * for `markReady()` instead of texture load.
     */
    skipTexture?: boolean;
  }

  let { children, skipTexture = false }: Props = $props();

  /**
   * The engine + its canvas live for the lifetime of this ModeFrame
   * instance. Each route creates a new ModeFrame → a new engine → fresh
   * texture load. Engine persistence across navigation isn't worth the
   * shared-state complexity it caused (real OOM crashes); revisit when
   * we have profiled evidence that re-init time is the bottleneck.
   */
  let canvasContainer: HTMLDivElement | undefined = $state();
  let engine = $state<MapEngine | null>(null);
  let revealed = $state<boolean>(false);
  let pendingYield: ReturnType<typeof setTimeout> | undefined;

  function markReady(): void {
    if (!revealed) revealed = true;
  }

  onMount(() => {
    if (!canvasContainer) return;
    // Defer engine construction by one task so the browser can paint
    // this ModeFrame's loading veil BEFORE the heavy WebGL setup
    // blocks the main thread.
    //
    // Without this, every cross-mode navigation runs in a single JS
    // task: click handler → Svelte re-render → {#key} unmount old →
    // onDestroy old.dispose() → mount new → new MapEngine() →
    // mode.onMount → loadBorders sync work. The browser gets no
    // paint frame until everything finishes, so the veil only flashes
    // at the very end. Yielding here gives a clean paint window with
    // the spinner already on screen before the heavy setup starts.
    const yieldHandle = setTimeout(() => {
      if (!canvasContainer) return;
      const instance = new MapEngine({
        container: canvasContainer,
        skipTexture,
        onTextureLoad: ({ tier, durationMs }) => {
          if (!revealed) {
            // Tiny delay so the texture's own paint settles before the
            // veil starts disappearing — avoids a one-frame flash of a
            // barely-textured globe.
            setTimeout(markReady, 80);
          }
          console.info(`[atlas] ${tier} texture ready in ${Math.round(durationMs)}ms`);
        },
        onTextureError: (err, tier) => {
          console.warn(`[atlas] texture tier ${tier} failed`, err);
        },
      });
      engine = instance;
    }, 0);
    // Cancel pending construction if the ModeFrame unmounts before
    // the deferred task fires (rapid double-click / fast navigation).
    pendingYield = yieldHandle;
  });

  onDestroy(() => {
    if (pendingYield) clearTimeout(pendingYield);
    engine?.dispose();
    engine = null;
  });
</script>

<div class="mode-frame">
  <div class="mode-frame__canvas" bind:this={canvasContainer}></div>

  <!-- Loading veil — fades out once the mode signals ready. -->
  <div class="mode-frame__loading" class:mode-frame__loading--hidden={revealed}>
    <div class="mode-frame__loading-spinner" aria-hidden="true"></div>
    <div class="mode-frame__loading-text">Loading globe</div>
  </div>

  <!-- Mode UI overlay. Only renders the child snippet once the engine is
       ready, so child components can use the engine in their own onMount
       without checking for null. -->
  <div class="mode-frame__overlay">
    {#if engine}
      {@render children(engine, markReady)}
    {/if}
  </div>
</div>
